import React, { useState, useMemo } from 'react'
import { Container, TripStatus } from '../../types/container'
import { mapTripStatusToBadgeVariant } from '../../services/containerService'
import { Table } from '../ui/Table/Table'
import { TableHeader } from '../ui/Table/TableHeader'
import { TableRow } from '../ui/Table/TableRow'
import { TableCell } from '../ui/Table/TableCell'
import { Badge } from '../ui/Badge'

export interface ContainerTableProps {
  data: Container[]
  onRowClick: (container: Container) => void
}

/**
 * ContainerTable component
 *
 * Displays containers in a tabular format with pagination controls.
 * Shows 6 columns: Container, Container Number, Type, Head, Head, Trip Status
 *
 * Features:
 * - Click rows to view trip details
 * - Status badges with color coding
 * - Configurable rows per page (10, 25, 50)
 * - Page number navigation
 */
interface GroupedRow {
  key: string
  type: string
  shippingLine: string
  count: number
  containers: Container[]
}

export const ContainerTable: React.FC<ContainerTableProps> = ({ data, onRowClick }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // Group containers by type + shipping line
  const groupedData = useMemo(() => {
    const groups = new Map<string, GroupedRow>()
    data.forEach(container => {
      const key = `${container.type}|${container.shippingLine}`
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          type: container.type,
          shippingLine: container.shippingLine,
          count: 0,
          containers: [],
        })
      }
      const group = groups.get(key)!
      group.count++
      group.containers.push(container)
    })
    return Array.from(groups.values()).sort((a, b) => b.count - a.count)
  }, [data])

  // Calculate pagination values
  const totalPages = Math.ceil(groupedData.length / rowsPerPage)
  const startIndex = (currentPage - 1) * rowsPerPage
  const endIndex = startIndex + rowsPerPage
  const paginatedData = groupedData.slice(startIndex, endIndex)

  // Generate page numbers for pagination controls
  const pageNumbers = useMemo(() => {
    const pages: number[] = []
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i)
    }
    return pages
  }, [totalPages])

  // Get trip status from container's latest trip
  const getTripStatus = (container: Container): TripStatus => {
    if (container.trips.length === 0) return TripStatus.PLANNED

    // Sort trips by date and get the most recent
    const sortedTrips = [...container.trips].sort(
      (a, b) => new Date(b.planningDate).getTime() - new Date(a.planningDate).getTime()
    )

    return sortedTrips[0].status
  }

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  // Handle rows per page change
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newRowsPerPage = Number(event.target.value)
    setRowsPerPage(newRowsPerPage)
    setCurrentPage(1) // Reset to first page when changing rows per page
  }

  // Map Dutch status labels
  const getStatusLabel = (status: TripStatus): string => {
    const statusLabels: Record<TripStatus, string> = {
      [TripStatus.PLANNED]: 'Gepland',
      [TripStatus.IN_PROGRESS]: 'In Uitvoering',
      [TripStatus.COMPLETED]: 'Voltooid',
      [TripStatus.CANCELLED]: 'Geannuleerd'
    }
    return statusLabels[status] || status
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader columns={3}>
          <TableCell>Type</TableCell>
          <TableCell>Shipping Line</TableCell>
          <TableCell>Aantal</TableCell>
        </TableHeader>

        {paginatedData.map((group) => (
            <TableRow
              key={group.key}
              columns={3}
              onClick={() => {
                const merged: Container = {
                  ...group.containers[0],
                  number: `${group.type} - ${group.shippingLine}`,
                  trips: group.containers.flatMap(c => c.trips),
                }
                onRowClick(merged)
              }}
            >
              <TableCell>{group.type}</TableCell>
              <TableCell>{group.shippingLine}</TableCell>
              <TableCell>{group.count}</TableCell>
            </TableRow>
        ))}

        {paginatedData.length === 0 && (
          <div className="px-8 py-12 text-center text-gray-500">
            Geen containers gevonden
          </div>
        )}
      </Table>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between px-4">
        {/* Rows per page selector */}
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <span>Rijen per pagina:</span>
          <select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>

        {/* Page info and navigation */}
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-700">
            Pagina {currentPage} van {totalPages}
          </span>

          <div className="flex gap-1">
            {/* Previous button */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Vorige
            </button>

            {/* Page number buttons */}
            {pageNumbers.map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-3 py-1 text-sm border rounded ${
                  page === currentPage
                    ? 'text-white'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
                style={page === currentPage ? { backgroundColor: '#DA0000', borderColor: '#DA0000' } : {}}
              >
                {page}
              </button>
            ))}

            {/* Next button */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Volgende
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
